import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Typography, Button, Spin, Alert, Empty, Tag, message, Modal } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, CheckCircleOutlined, ClockCircleOutlined, StopOutlined } from '@ant-design/icons';

import { presencasService } from '../../services/api';
import { usePermissaoRecurso } from '../../hooks/usePermissoes';
import { buildApiErrorMessage } from '../../utils/apiError';
import AlunoChamadaLinha from '../shared/AlunoChamadaLinha';
import '../../styles/PresencasAdmin.css';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────
// Contrato real (backend, Fase A/B/C): ChamadaAdminResponse (GET/PUT
// .../chamada) NÃO inclui nome_turma/nome_unidade/nome_modalidade/
// nome_equipe -- só turma_id/equipe_id (ver app/schemas/presencas_admin.py).
// Esses nomes só existem em EncontroAdminResponse (a linha da
// listagem). Em vez de alterar o backend só para isso, a linha clicada
// na listagem é repassada via `location.state` (React Router) --
// puramente para exibição no cabeçalho, NUNCA como fonte de verdade de
// autorização/regra: se o state estiver ausente (ex.: acesso direto por
// URL/F5), o cabeçalho cai para os IDs devolvidos pelo próprio GET
// chamada, sem quebrar a tela.
// ─────────────────────────────────────────────────────────────

const STATUS_ENCONTRO_INFO = {
  PREVISTA: { label: 'Chamada pendente', color: 'gold', icon: <ClockCircleOutlined /> },
  REALIZADA: { label: 'Chamada realizada', color: 'green', icon: <CheckCircleOutlined /> },
  CANCELADA: { label: 'Aula cancelada', color: 'default', icon: <StopOutlined /> },
};

const MENSAGEM_NAO_ENCONTRADO = 'Esta aula não está disponível ou não pertence ao seu escopo de acesso.';

const formatarHora = (valor) => (valor ? valor.slice(0, 5) : '--:--');

const formatarData = (valor) => {
  if (!valor) return '';
  const [ano, mes, dia] = valor.split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : valor;
};

const formatarDataHora = (valor) => {
  if (!valor) return null;
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return valor;
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const normalizarObservacao = (valor) => {
  if (valor == null) return null;
  const limpa = valor.trim();
  return limpa === '' ? null : limpa;
};

const paraAlunoLocal = (aluno) => ({
  matricula_id: aluno.matricula_id,
  aluno_id: aluno.aluno_id,
  nome_completo: aluno.nome_completo,
  status_frequencia: aluno.status_frequencia,
  observacao: aluno.observacao,
});

const ChamadaAdminDetalhe = () => {
  const { encontroId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { loading: permissaoLoading, isAdmin, podeVisualizar, podeEditar } = usePermissaoRecurso('presencas');

  // Dados de apresentação vindos da listagem (opcionais, nunca fonte de
  // verdade -- ver comentário de contrato acima).
  const linhaOrigem = location.state?.linha || null;
  const filtrosOrigem = location.state?.filtros || null;

  const turmaIdQuery = searchParams.get('turma_id');
  const dataQuery = searchParams.get('data');
  const fluxoMaterializacao = !encontroId; // /presencas/nova?turma_id=&data=

  const [encontroIdAtual, setEncontroIdAtual] = useState(encontroId ? Number(encontroId) : null);
  const [chamada, setChamada] = useState(null); // baseline do servidor (GET/PUT)
  const [alunosLocal, setAlunosLocal] = useState([]);
  const [observacoesAbertas, setObservacoesAbertas] = useState(() => new Set());

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // erro de carregamento/materialização
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState(null); // { status, message, podeAtualizar }
  const [precisaAtualizarAntes, setPrecisaAtualizarAntes] = useState(false); // 409

  const sincronizarComServidor = useCallback((dadosChamada) => {
    setChamada(dadosChamada);
    const alunos = (dadosChamada.alunos || []).map(paraAlunoLocal);
    setAlunosLocal(alunos);
    setObservacoesAbertas(
      new Set(alunos.filter((a) => a.observacao && a.observacao.trim() !== '').map((a) => a.matricula_id))
    );
  }, []);

  // Carrega a chamada -- combina os dois fluxos (A: encontro já
  // materializado; B: materializar sob demanda antes) num ÚNICO loading
  // contínuo, sem placeholder intermediário entre o PUT e o GET.
  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    setErroSalvar(null);
    setPrecisaAtualizarAntes(false);
    try {
      let idParaBuscar = encontroId ? Number(encontroId) : null;

      if (fluxoMaterializacao) {
        if (!turmaIdQuery || !dataQuery) {
          setErro({ status: null, message: 'Turma e data são obrigatórias para iniciar a chamada.' });
          setLoading(false);
          return;
        }
        const respPreparar = await presencasService.prepararEncontro({
          turma_id: Number(turmaIdQuery),
          data: dataQuery,
        });
        idParaBuscar = respPreparar.data.encontro_id;
        setEncontroIdAtual(idParaBuscar);
        // Troca a URL para a forma canônica (/presencas/:id), preservando
        // o state de navegação -- idempotente mesmo se o usuário voltar
        // e reabrir /presencas/nova de novo com os mesmos parâmetros.
        navigate(`/presencas/${idParaBuscar}`, { replace: true, state: location.state });
      }

      const response = await presencasService.obterChamada(idParaBuscar);
      sincronizarComServidor(response.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setErro({ status, message: MENSAGEM_NAO_ENCONTRADO });
      } else if (status === 422) {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível preparar esta aula. Verifique se a data pertence à grade da turma.'
        );
        setErro({ status, message: mensagem });
      } else {
        const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível carregar a chamada. Tente novamente.');
        setErro({ status, message: mensagem });
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encontroId, fluxoMaterializacao, turmaIdQuery, dataQuery]);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encontroId]);

  // ── Derivados do state local (nunca dos contadores antigos do GET) ──
  const contadores = alunosLocal.reduce(
    (acc, a) => {
      if (a.status_frequencia === 'PRESENTE') acc.presentes += 1;
      else if (a.status_frequencia === 'AUSENTE') acc.ausentes += 1;
      else if (a.status_frequencia === 'JUSTIFICADA') acc.justificadas += 1;
      else acc.naoInformadas += 1;
      return acc;
    },
    { presentes: 0, ausentes: 0, justificadas: 0, naoInformadas: 0 }
  );
  const chamadaCompleta = contadores.naoInformadas === 0;

  const baselineAlunos = chamada ? (chamada.alunos || []).map(paraAlunoLocal) : [];
  const baselineByMatricula = new Map(baselineAlunos.map((a) => [a.matricula_id, a]));
  const houveAlteracao = alunosLocal.some((local) => {
    const base = baselineByMatricula.get(local.matricula_id);
    if (!base) return true;
    return (
      local.status_frequencia !== base.status_frequencia ||
      normalizarObservacao(local.observacao) !== normalizarObservacao(base.observacao)
    );
  });

  const statusEncontro = chamada?.status_encontro;
  const cancelada = statusEncontro === 'CANCELADA';
  // CANCELADA é sempre somente leitura, mesmo com podeEditar -- reflete
  // EncontroCanceladoError já existente no backend (salvar_chamada
  // rejeita qualquer escrita nesse estado), não uma regra nova.
  const somenteLeitura = cancelada || !podeEditar;

  // ── Handlers de edição (somente state local, sem chamada de rede) ──
  const handleMudarStatus = (matriculaId, novoStatus) => {
    setAlunosLocal((atual) =>
      atual.map((a) => (a.matricula_id === matriculaId ? { ...a, status_frequencia: novoStatus } : a))
    );
  };

  const handleMudarObservacao = (matriculaId, texto) => {
    setAlunosLocal((atual) =>
      atual.map((a) => (a.matricula_id === matriculaId ? { ...a, observacao: texto } : a))
    );
  };

  const handleAbrirObservacao = (matriculaId) => {
    setObservacoesAbertas((atual) => new Set(atual).add(matriculaId));
  };

  const handleMarcarTodosPresentes = () => {
    // Só preenche quem está NAO_INFORMADA -- nunca sobrescreve
    // AUSENTE/JUSTIFICADA/PRESENTE já marcados.
    setAlunosLocal((atual) =>
      atual.map((a) => (a.status_frequencia === 'NAO_INFORMADA' ? { ...a, status_frequencia: 'PRESENTE' } : a))
    );
  };

  // ── Salvamento ──
  const handleSalvar = async () => {
    if (salvando) return; // impede duplo clique
    // Defesa programática: nunca envia PUT com algum aluno ainda
    // NAO_INFORMADA -- independe do botão estar desabilitado.
    if (!chamadaCompleta || somenteLeitura) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const registros = alunosLocal.map((a) => ({
        matricula_id: a.matricula_id,
        status: a.status_frequencia,
        observacao: normalizarObservacao(a.observacao),
      }));
      const response = await presencasService.salvarChamada(encontroIdAtual, { registros });
      sincronizarComServidor(response.data);
      setPrecisaAtualizarAntes(false);
      message.success(
        response.data.status_encontro === 'REALIZADA' && chamada?.status_encontro === 'PREVISTA'
          ? 'Chamada finalizada com sucesso.'
          : 'Correções salvas com sucesso.'
      );
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setErroSalvar({ status, message: MENSAGEM_NAO_ENCONTRADO });
      } else if (status === 422) {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível salvar a chamada. Revise os dados e tente novamente.'
        );
        setErroSalvar({ status, message: mensagem, podeAtualizar: true });
      } else if (status === 409) {
        const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível salvar por conflito de dados.');
        setErroSalvar({ status, message: mensagem, podeAtualizar: true });
        setPrecisaAtualizarAntes(true);
      } else {
        const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível salvar a chamada. Tente novamente.');
        setErroSalvar({ status, message: mensagem });
      }
    } finally {
      setSalvando(false);
    }
  };

  // ── Navegação ──
  const voltarParaListagem = () => navigate('/presencas', { state: { filtros: filtrosOrigem } });

  const handleVoltar = () => {
    if (!somenteLeitura && houveAlteracao) {
      Modal.confirm({
        title: 'Descartar alterações?',
        content: 'Você fez alterações na chamada que ainda não foram salvas. Deseja realmente sair sem salvar?',
        okText: 'Sair sem salvar',
        cancelText: 'Continuar editando',
        okButtonProps: { danger: true },
        onOk: voltarParaListagem,
      });
      return;
    }
    voltarParaListagem();
  };

  if (permissaoLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" tip="Validando permissões..." />
      </div>
    );
  }

  if (!isAdmin && !podeVisualizar) {
    return <Navigate to="/acesso-negado" replace />;
  }

  const statusInfo = STATUS_ENCONTRO_INFO[statusEncontro] || { label: statusEncontro, color: 'default', icon: null };
  const botaoPrincipalTexto = statusEncontro === 'PREVISTA' ? 'Finalizar chamada' : 'Salvar correções';
  const botaoPrincipalDesabilitado =
    salvando ||
    precisaAtualizarAntes ||
    !chamadaCompleta ||
    (statusEncontro !== 'PREVISTA' && !houveAlteracao);

  return (
    <div style={{ padding: 24, maxWidth: 760, margin: '0 auto' }}>
      <Button icon={<ArrowLeftOutlined />} type="text" onClick={handleVoltar} style={{ marginBottom: 12 }}>
        Voltar para Presenças
      </Button>

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 48 }} role="status" aria-live="polite">
          <Spin size="large" />
          <Text type="secondary">{fluxoMaterializacao ? 'Preparando aula...' : 'Carregando chamada...'}</Text>
        </div>
      )}

      {!loading && erro && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 48 }}>
          <Alert
            type={erro.status === 404 ? 'warning' : 'error'}
            showIcon
            message="Não foi possível abrir a chamada"
            description={erro.message}
            style={{ maxWidth: 480, textAlign: 'left' }}
          />
          {erro.status !== 404 && (
            <Button icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          )}
        </div>
      )}

      {!loading && !erro && chamada && (
        <div>
          <div className="presencas-chamada__header">
            <Title level={4} style={{ marginBottom: 0 }}>
              {linhaOrigem?.nome_turma || `Turma #${chamada.turma_id}`}
            </Title>
            <div className="presencas-chamada__header-grid">
              <Text><Text type="secondary">Data: </Text>{formatarData(chamada.data)}</Text>
              <Text><Text type="secondary">Horário: </Text>{formatarHora(chamada.hora_inicio)}–{formatarHora(chamada.hora_fim)}</Text>
              <Text><Text type="secondary">Unidade: </Text>{linhaOrigem?.nome_unidade || '—'}</Text>
              <Text><Text type="secondary">Modalidade: </Text>{linhaOrigem?.nome_modalidade || '—'}</Text>
              <Text><Text type="secondary">Professor: </Text>{linhaOrigem?.nome_equipe || `Equipe #${chamada.equipe_id}`}</Text>
              <div>
                <Tag icon={statusInfo.icon} color={statusInfo.color}>{statusInfo.label}</Tag>
              </div>
            </div>
            {chamada.chamada_fechada_em && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Fechada em {formatarDataHora(chamada.chamada_fechada_em)}
              </Text>
            )}
            <div style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }} aria-live="polite">
              <Text>Presentes: {contadores.presentes}</Text>
              <Text>Ausentes: {contadores.ausentes}</Text>
              <Text>Justificadas: {contadores.justificadas}</Text>
              <Text>Não informadas: {contadores.naoInformadas}</Text>
            </div>
          </div>

          {cancelada && (
            <Alert
              type="default"
              showIcon
              icon={<StopOutlined />}
              message="Aula cancelada"
              description="Esta aula foi cancelada. A chamada não pode ser editada."
              style={{ marginBottom: 16 }}
            />
          )}

          {!cancelada && !podeEditar && (
            <Alert
              type="info"
              showIcon
              message="Modo somente visualização"
              description="Seu perfil não tem permissão para registrar ou corrigir esta chamada."
              style={{ marginBottom: 16 }}
            />
          )}

          {erroSalvar && (
            <Alert
              type={erroSalvar.status === 404 ? 'warning' : 'error'}
              showIcon
              message="Não foi possível salvar"
              description={erroSalvar.message}
              style={{ marginBottom: 16 }}
              action={
                erroSalvar.podeAtualizar ? (
                  <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
                    Atualizar chamada
                  </Button>
                ) : undefined
              }
            />
          )}

          {!somenteLeitura && !chamadaCompleta && (
            <Alert
              type="info"
              showIcon
              message={`${contadores.naoInformadas} aluno${contadores.naoInformadas > 1 ? 's' : ''} ainda sem presença informada.`}
              style={{ marginBottom: 16 }}
            />
          )}

          {!somenteLeitura && contadores.naoInformadas > 0 && (
            <Button onClick={handleMarcarTodosPresentes} style={{ marginBottom: 16 }}>
              Marcar todos como presentes
            </Button>
          )}

          {alunosLocal.length === 0 ? (
            <Empty description="Nenhum aluno elegível para esta aula." />
          ) : (
            <div className="presencas-chamada__alunos-lista">
              {alunosLocal.map((aluno) => (
                <AlunoChamadaLinha
                  key={aluno.matricula_id}
                  aluno={aluno}
                  somenteLeitura={somenteLeitura}
                  observacaoAberta={observacoesAbertas.has(aluno.matricula_id)}
                  onMudarStatus={handleMudarStatus}
                  onMudarObservacao={handleMudarObservacao}
                  onAbrirObservacao={handleAbrirObservacao}
                />
              ))}
            </div>
          )}

          {!somenteLeitura && (
            <Button
              type="primary"
              size="large"
              block
              loading={salvando}
              disabled={botaoPrincipalDesabilitado}
              onClick={handleSalvar}
              style={{ marginTop: 16, minHeight: 44 }}
            >
              {botaoPrincipalTexto}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ChamadaAdminDetalhe;
